/**
 * ADR-0242 — the logo beside a name on the Agents page.
 *
 * Same resolution as the visits panel (`AgentLogo`). A row whose verdict
 * came from a spoof signal (impersonation evidence) gets the generic coral
 * bot glyph, never the brand whose name it used — the same rule as a
 * Mismatch row in the visits panel.
 */

import { Bot } from 'lucide-react';

import type { AgentRollupSummary } from '@sill/types/api';

import { useC } from '../../lib/theme.js';
import { AgentLogo } from '../mandates/AgentLogo.js';

type Row = Pick<AgentRollupSummary, 'agent_id' | 'visitor_class' | 'verdict_source' | 'inferred_name'>;

export function AgentRowLogo({ agent, name, size = 16 }: { agent: Row; name: string; size?: number }): JSX.Element {
  const C = useC();
  if (agent.verdict_source === 'spoof') {
    return <Bot size={size} color={C.coralFg} style={{ flexShrink: 0 }} aria-label="Unknown sender" />;
  }
  const vc = agent.visitor_class;
  const visitor_class = vc === 'matched_agent' || vc === 'unknown_agent' || vc === 'human_likely' ? vc : undefined;
  return (
    <AgentLogo
      agent_id={agent.agent_id}
      {...(visitor_class !== undefined ? { visitor_class } : {})}
      size={size}
      fallbackColor={C.muted}
      label={agent.inferred_name ?? name}
    />
  );
}
